import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listValets, getValetEfficiency } from '../services/api';
import './Valets.css';

export default function Valets() {
  const [valets, setValets] = useState([]);
  const [topPerf, setTopPerf] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { valets: list } = await listValets({ per_page: 100 });
        setValets(
          list.map(v => ({
            id: `#${v.id}`,
            name: v.name,
            monthlyTips: null,
            reviewScore: null,
            efficiency: v.current_ride_count + ' Cars/active',
          }))
        );

        const { results } = await getValetEfficiency({ top_n: 1, period: '30_days' });
        if (results.length) {
          const tp = results[0];
          setTopPerf({
            name: tp.valet_name,
            review: null,
            tips: null,
            averageEfficiency: `${tp.avg_completion_time_minutes ?? '?'} min/ride`,
          });
        }
      } catch (e) {
        console.error(e);
        setError('Could not load valet data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sortedValets = useMemo(() => {
    const copy = [...valets];
    if (sortBy === 'name') copy.sort((a, b) => a.name.localeCompare(b.name));
    else copy.sort((a, b) => (b.monthlyTips ?? 0) - (a.monthlyTips ?? 0));
    return copy;
  }, [valets, sortBy]);

  if (loading) return <p className="pad">Loading valets…</p>;
  if (error) return <p className="pad error">{error}</p>;

  return (
    <div className="page">
      {/* Registration code + Top performer */}
      <div className="row">
        <div className="card flex-between">
          <div>
            <h3>Registration Code</h3>
            <p className="muted">1234-3728-7529-8374</p>
          </div>
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d0/QR_code_for_mobile_English_Wikipedia.svg/330px-QR_code_for_mobile_English_Wikipedia.svg.png"
            alt="QR Code"
            className="qr"
          />
        </div>

        <div className="card flex-between">
          <div>
            <h3>Top Performer</h3>
            {topPerf ? (
              <>
                <p>Name: {topPerf.name}</p>
                <p>Review: {topPerf.review ?? '—'}</p>
                <p>Tips: {topPerf.tips ?? '—'}</p>
                <p>Avg. Efficiency: {topPerf.averageEfficiency}</p>
              </>
            ) : (
              <p>No data yet</p>
            )}
          </div>
          <img
            src="https://media.sproutsocial.com/uploads/2022/06/profile-picture.jpeg"
            alt="Top Performer"
            className="avatar-lg"
          />
        </div>
      </div>

      {/* Sort controls */}
      <div className="sort-controls">
        <span className="label">Sort By:</span>
        <button
          className={`btn ${sortBy === 'name' ? 'btn-primary' : 'btn-chip'}`}
          onClick={() => setSortBy('name')}
        >
          Name
        </button>
        <button
          className={`btn ${sortBy === 'tips' ? 'btn-primary' : 'btn-chip'}`}
          onClick={() => setSortBy('tips')}
        >
          Monthly Tips
        </button>
      </div>

      {/* Table */}
      <div className="card">
        <h2>Valets</h2>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Valet</th>
                <th>Monthly Tips ($)</th>
                <th>Review Score</th>
                <th>Average Efficiency</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedValets.map(v => (
                <tr key={v.id}>
                  <td className="valet-cell">
                    <img
                      src="https://media.sproutsocial.com/uploads/2022/06/profile-picture.jpeg"
                      alt="Valet"
                      className="avatar-sm"
                    />
                    <div>
                      <p className="valet-name">{v.name}</p>
                      <span className="muted">{v.id}</span>
                    </div>
                  </td>
                  <td>{v.monthlyTips ?? '—'}</td>
                  <td>{v.reviewScore ?? '—'}</td>
                  <td>{v.efficiency}</td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/valet/${v.id.replace('#', '')}`)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
              {sortedValets.length === 0 && (
                <tr>
                  <td colSpan="5" className="empty">No valets to display.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
